import React, { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import InputAdornment from "@mui/material/InputAdornment"

import {
  ContentContainer,
  SearchInput,
  LastTransfersTitle,
  ClientContainer,
  StyledButton,
} from "./styled"
import UIIcon from "../../components/UIIcon"
import PhoneItem from "../../components/PhoneItem"
import { formatPhoneNumber } from "../../utils/formatters"
import { setRecipient } from "../../store/slices/c2cSlice"
import { useContactSearchInput } from "../../hooks/useContactsSearchInput"
import {
  filterContactsByPhoneAndName,
  isPhoneNumber,
} from "../../utils/contacts"
import LoadingOverlay from "../../components/LoadingOverlay"
import {
  setCurrentFlow,
  setCurrentStep,
  setNextStep,
} from "../../store/slices/routingSlice/routingSlice"
import { ContactsTransfersHistoryProps } from "./props"
import { RootState } from "../../store/store"
import { STEPS_NAME } from "../../store/slices/routingSlice/flowsSteps"
import { useGetRecentOperations } from "../../api/hooks/useGetRecentOperations"
import { OperationType } from "../../api/interface"

const ContactsTransfersHistory = ({
  handleSelectContact,
  isLoading: isLoadingDefaultBank,
  handleClickNext,
}: ContactsTransfersHistoryProps) => {
  const dispatch = useDispatch()
  const { data: recentOperations } = useGetRecentOperations(100, OperationType.SBP_OUTCOME_TRANSFER)

  const uniquePhones: string[] = []

  recentOperations?.forEach((operation) => {
    const recipientPhoneNumber = operation.fields.find((f) => f.name === "RecipientPhoneNumber") 
    if (recipientPhoneNumber) {
      if (!uniquePhones.includes(recipientPhoneNumber.value))
        uniquePhones.push(recipientPhoneNumber.value)
    }
  })

  const transferTo = useSelector(
    (state: RootState) => state.transferForm.recipient,
  )

  const { contactQuery, onContactQueryChange } = useContactSearchInput({
    isPhoneNumberInput: true,
  })

  const lastContacts = useMemo(
    () =>
      uniquePhones.map((item) => ({
        phone: item,
        name: "Имя Фамилия",
      })),
    [uniquePhones],
  )

  const filteredContacts = useMemo(
    () => filterContactsByPhoneAndName(lastContacts, contactQuery),
    [lastContacts, contactQuery],
  )

  const handleClickContacts = () => {
    dispatch(setNextStep(STEPS_NAME.c2cPush.getContacts))
  }

  const handleChangeQuery = (
    evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    dispatch(setRecipient({ ...transferTo, phone: value }))
    onContactQueryChange(value)
  }

  const isPhoneValid = isPhoneNumber(contactQuery)

  useEffect(() => {
    dispatch(setCurrentFlow("c2cPush"))
    dispatch(setCurrentStep(STEPS_NAME.c2cPush.getLastTransfers))
  }, [])

  if (isLoadingDefaultBank) {
    return <LoadingOverlay />
  }

  return (
    <ContentContainer>
      <SearchInput
        placeholder="Номер получателя"
        value={contactQuery}
        onChange={handleChangeQuery}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <UIIcon
                iconName="account"
                size={24}
                onClick={handleClickContacts}
              />
            </InputAdornment>
          ),
        }}
      />
      <LastTransfersTitle variant="h4Bold">
        Последние переводы
      </LastTransfersTitle>

      <ClientContainer>
        {filteredContacts.map((contact) => (
          <PhoneItem
            sx={{ mb: 1 }}
            key={contact.id!}
            phone={formatPhoneNumber(contact.phone)}
            onClick={() => handleSelectContact(contact)}
          />
        ))}
      </ClientContainer>
      <StyledButton
        variant="contained"
        onClick={handleClickNext}
        disabled={!isPhoneValid}
      >
        Продолжить
      </StyledButton>
    </ContentContainer>
  )
}

export default ContactsTransfersHistory
